 
import { Users, User as UserIcon, Search } from "lucide-react";
import { useMemo } from "react";
import type { User } from "@harmonyso/tenants-sdk";

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UserSearchBar } from "@/components/tenants/user-search-bar";
import { UserTable } from "@/components/tenants/user-table";
import { PaginationControls } from "@/components/tenants/pagination-controls";

interface TenantUsersSectionProps {
  usersData: { items: User[]; total_count: number } | undefined;
  usersLoading: boolean;
  usersError: Error | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  tenantId: string;
  onImpersonate?: (tenantId: string, loginId: string) => void;
  isImpersonating: boolean;
}

export function TenantUsersSection({
  usersData,
  usersLoading,
  usersError,
  searchQuery,
  onSearchChange,
  currentPage,
  onPageChange,
  itemsPerPage,
  tenantId,
  onImpersonate,
  isImpersonating,
}: TenantUsersSectionProps) {
  // Filter and paginate users
  const filteredAndPaginatedUsers = useMemo(() => {
    if (!usersData?.items) return { items: [], totalPages: 0, totalCount: 0 };

    // Filter users based on search query
    const filteredUsers = usersData.items.filter((user: User) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.display_name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.login_ids?.some((loginId) =>
          loginId.toLowerCase().includes(searchLower),
        ) ||
        user.roles?.some((role) => role.toLowerCase().includes(searchLower))
      );
    });

    // Calculate pagination
    const totalCount = filteredUsers.length;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    return {
      items: paginatedUsers,
      totalPages,
      totalCount,
    };
  }, [usersData?.items, searchQuery, currentPage, itemsPerPage]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Tenant Users
        </CardTitle>
        <CardDescription>Users who have access to this tenant</CardDescription>
      </CardHeader>
      <CardContent>
        {usersLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
          </div>
        ) : usersError ? (
          <div className="py-8 text-center">
            <UserIcon className="mx-auto mb-4 h-12 w-12 text-red-500" />
            <h3 className="mb-2 text-lg font-semibold">Failed to Load Users</h3>
            <p className="text-muted-foreground">
              {usersError.message || "Could not load users for this tenant"}
            </p>
          </div>
        ) : usersData?.items.length === 0 ? (
          <div className="py-8 text-center">
            <UserIcon className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
            <h3 className="mb-2 text-lg font-semibold">No Users Found</h3>
            <p className="text-muted-foreground">
              This tenant doesn't have any users yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <UserSearchBar
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              totalCount={usersData?.total_count ?? 0}
              filteredCount={filteredAndPaginatedUsers.totalCount}
            />

            {filteredAndPaginatedUsers.totalCount === 0 && searchQuery ? (
              <div className="py-8 text-center">
                <Search className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                <h3 className="mb-2 text-lg font-semibold">No Users Found</h3>
                <p className="text-muted-foreground">
                  No users match your search for "{searchQuery}".
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onSearchChange("");
                  }}
                  className="mt-4"
                >
                  Clear Search
                </Button>
              </div>
            ) : (
              <>
                <UserTable
                  users={filteredAndPaginatedUsers.items}
                  tenantId={tenantId}
                  onImpersonate={onImpersonate}
                  isImpersonating={isImpersonating}
                />
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={filteredAndPaginatedUsers.totalPages}
                  onPageChange={onPageChange}
                />
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
